const PAYMENT_METHOD_LABELS = {
  upi: "UPI",
  card: "Card",
  credit: "Credit Card",
  debit: "Debit Card",
  netbanking: "Net Banking",
  wallet: "Wallet",
  emi: "EMI",
  paylater: "Pay Later",
  bank_transfer: "Bank Transfer",
  manual: "Manual Verification",
};

export function isManualPaymentRecord(payment) {
  if (!payment) return false;
  return (
    payment.payment_source === "manual" ||
    String(payment.payment_method || "").toLowerCase() === "manual" ||
    String(payment.razorpay_order_id || "").startsWith("manual_")
  );
}

export function shouldShowRazorpayOrderId(payment) {
  return Boolean(payment?.razorpay_order_id) && !isManualPaymentRecord(payment);
}

export function resolvePaymentLogOrderReference(payment) {
  if (!payment) return "—";
  if (isManualPaymentRecord(payment)) {
    return payment.reference_number?.trim() || "—";
  }
  return payment.razorpay_order_id || "—";
}

export function resolvePaymentHistorySummary(payment) {
  if (!payment) return "—";
  if (isManualPaymentRecord(payment)) {
    if (payment.reference_number?.trim()) {
      return `Ref: ${payment.reference_number.trim()}`;
    }
    return "Manual payment";
  }
  return payment.razorpay_payment_id || payment.razorpay_order_id || "—";
}

export function formatPaymentMethod(method, payment = null) {
  if (method) {
    const key = String(method).trim().toLowerCase();
    if (PAYMENT_METHOD_LABELS[key]) return PAYMENT_METHOD_LABELS[key];
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  if (isManualPaymentRecord(payment)) return "Manual Verification";
  if (payment?.razorpay_payment_id) return "Online Payment (Razorpay)";
  if (payment?.razorpay_order_id && !String(payment.razorpay_order_id).startsWith("manual_")) {
    return "Online Payment (Razorpay)";
  }

  return "—";
}

export function resolveTransactionId(payment) {
  if (!payment) return "—";
  const txnId = payment.razorpay_payment_id;
  if (txnId && String(txnId).trim()) return String(txnId).trim();
  return "—";
}

export function formatPaymentDateTime(payment) {
  if (!payment) return "—";
  const raw =
    payment?.paid_at ||
    (payment?.status === "success" || payment?.status === "partial_success"
      ? payment?.updatedAt
      : null) ||
    payment?.createdAt;
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatPaymentAmount(amount, currency = "INR") {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(num);
}

export function resolveRazorpaySuccessPayment(payments = []) {
  if (!Array.isArray(payments) || !payments.length) return null;
  return (
    payments.find(
      (payment) =>
        String(payment?.status).toLowerCase() === "success" &&
        payment?.razorpay_payment_id &&
        !isManualPaymentRecord(payment)
    ) || null
  );
}

export function resolveManualPaymentWithDetails(payments = []) {
  if (!Array.isArray(payments) || !payments.length) return null;

  const withDetails = payments.find(
    (payment) =>
      isManualPaymentRecord(payment) &&
      (payment?.payment_proof || payment?.reference_number || payment?.payment_note)
  );
  if (withDetails) return withDetails;

  return payments.find((payment) => isManualPaymentRecord(payment)) || null;
}

/** Whether the order has any completed payment (full, partial, or verified attempt). */
export function hasOrderPaymentReceived(order, payments = []) {
  if (!order) return false;

  const paymentStatus = String(order.payment_status || "").toLowerCase();
  if (paymentStatus === "paid" || paymentStatus === "partial_paid") {
    return true;
  }

  if (String(order.order_status || "").toLowerCase() === "paid") {
    return true;
  }

  if (!Array.isArray(payments) || !payments.length) return false;

  return payments.some((payment) => {
    const status = String(payment?.status || "").toLowerCase();
    return status === "success" || status === "partial_success";
  });
}

export function resolveSuccessfulPayment(payments = []) {
  const razorpaySuccess = resolveRazorpaySuccessPayment(payments);
  if (razorpaySuccess) return razorpaySuccess;

  if (!Array.isArray(payments) || !payments.length) return null;
  return (
    payments.find((payment) => String(payment?.status).toLowerCase() === "success") || null
  );
}

export function resolvePrimaryPaymentRecord(payments = []) {
  if (!Array.isArray(payments) || !payments.length) return null;

  const razorpaySuccess = resolveRazorpaySuccessPayment(payments);
  if (razorpaySuccess) return razorpaySuccess;

  const manualWithDetails = resolveManualPaymentWithDetails(payments);
  if (manualWithDetails) return manualWithDetails;

  const withDetails = payments.find(
    (payment) =>
      payment?.payment_proof || payment?.reference_number || payment?.payment_note
  );
  if (withDetails) return withDetails;

  return payments[0] || null;
}

export function resolveInvoiceId(order, payment = null) {
  if (payment?.invoice_id) return String(payment.invoice_id);
  if (order?.invoice_id && String(order.invoice_id).trim()) {
    return String(order.invoice_id).trim();
  }
  if (order?.order_number) return String(order.order_number);
  if (order?.id) return `INV-${String(order.id).slice(0, 8).toUpperCase()}`;
  return "—";
}

/** Legacy orders may still have proof fields on the order row (pre-migration). */
function buildDetailsFromLegacyOrder(order) {
  const hasLegacy =
    order?.payment_proof || order?.reference_number || order?.payment_note;
  if (!hasLegacy) return null;

  return {
    invoiceId: resolveInvoiceId(order),
    transactionId: "—",
    paymentMode: "Manual Verification",
    paymentAmount: order?.grand_total,
    paymentDate: order?.updatedAt
      ? formatPaymentDateTime({ createdAt: order.updatedAt })
      : "—",
    paymentProof: order?.payment_proof || null,
    referenceNumber: order?.reference_number || null,
    paymentNote: order?.payment_note || null,
    source: "manual",
  };
}

function pickFirstValue(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return null;
}

export function resolvePaymentDetails(order, payments = []) {
  const razorpayPayment = resolveRazorpaySuccessPayment(payments);
  const manualPayment = resolveManualPaymentWithDetails(payments);
  const primary = resolvePrimaryPaymentRecord(payments);

  if (!primary && !manualPayment) {
    const legacy = buildDetailsFromLegacyOrder(order);
    if (legacy) return legacy;

    return {
      invoiceId: resolveInvoiceId(order),
      transactionId: "—",
      paymentMode: "—",
      paymentAmount: order?.grand_total,
      paymentDate: "—",
      paymentProof: null,
      referenceNumber: null,
      paymentNote: null,
      source: null,
    };
  }

  const financialPayment = razorpayPayment || primary || manualPayment;
  const manualOnly = manualPayment && !razorpayPayment ? manualPayment : manualPayment;

  return {
    invoiceId: resolveInvoiceId(order, financialPayment),
    transactionId: razorpayPayment
      ? resolveTransactionId(razorpayPayment)
      : resolveTransactionId(financialPayment),
    paymentMode: razorpayPayment
      ? formatPaymentMethod(razorpayPayment.payment_method, razorpayPayment)
      : formatPaymentMethod(financialPayment?.payment_method, financialPayment),
    paymentAmount: financialPayment?.amount ?? order?.grand_total,
    paymentDate: formatPaymentDateTime(razorpayPayment || financialPayment),
    paymentProof: pickFirstValue(
      manualOnly?.payment_proof,
      manualPayment?.payment_proof,
      financialPayment?.payment_proof
    ),
    referenceNumber: pickFirstValue(
      manualOnly?.reference_number,
      manualPayment?.reference_number,
      financialPayment?.reference_number
    ),
    paymentNote: pickFirstValue(
      manualOnly?.payment_note,
      manualPayment?.payment_note,
      financialPayment?.payment_note
    ),
    source: razorpayPayment ? "razorpay" : isManualPaymentRecord(financialPayment) ? "manual" : null,
  };
}

export function hasStoredPaymentDetails(order, payments = []) {
  const details = resolvePaymentDetails(order, payments);
  return Boolean(
    details.paymentProof ||
      details.referenceNumber ||
      details.paymentNote ||
      (details.transactionId && details.transactionId !== "—") ||
      (details.paymentMode && details.paymentMode !== "—")
  );
}

export function orderHasManualPaymentRecord(payments = []) {
  return payments.some((payment) => isManualPaymentRecord(payment));
}
